import {
  all,
  call,
  put,
} from 'redux-saga/effects';
import { rejectActionPromise } from '../../../utils/rejectActionPromise';
import { resolveActionPromise } from '../../../utils/resolveActionPromise';
import { CONNECTION_ERROR } from '../../../configs/serverStatusCodes';
import makeRequest, { getBodyJson } from '../../../utils/makeRequest';
import processError from '../../../utils/processError';
import {
  loadMonitorListFailed,
  loadMonitorListSuccessful,
  loadMonitorListBegin,
} from '../actions';
import { DEFAULT_ITEMS_OF_PAGE } from '../../../configs';
import {
  SERVICE_HEALTH_CHECKS,
  SERVICE_KEYS,
  isServiceUnavailableStatus,
} from '../../System/serviceAvailability';

const SERVICE_TITLES = {
  [SERVICE_KEYS.SESSION_LOG]: 'Модуль сессий',
  [SERVICE_KEYS.PAYMENT]: 'Модуль платежей',
  [SERVICE_KEYS.CLIENTS]: 'Модуль клиентов',
  [SERVICE_KEYS.REPORTS]: 'Модуль отчетов',
  [SERVICE_KEYS.ZONES]: 'Модуль парковочных зон',
  [SERVICE_KEYS.TARIFF]: 'Модуль тарифов',
  [SERVICE_KEYS.SUBSCRIPTION]: 'Модуль абонементов',
  [SERVICE_KEYS.SUBSCRIPTION_BLACK_WHITE]: 'Модуль черного списка',
  [SERVICE_KEYS.EVENT_LOG]: 'Модуль событий',
  [SERVICE_KEYS.HOSTS]: 'Модуль хостов',
  [SERVICE_KEYS.SERVICE_MONITORING]: 'Модуль Backend API',
  [SERVICE_KEYS.USER_ACTION_LOG]: 'Модуль журнала действий пользователей',
};

const SERVICE_CODES = {
  [SERVICE_KEYS.SESSION_LOG]: 'session',
  [SERVICE_KEYS.PAYMENT]: 'payment',
  [SERVICE_KEYS.CLIENTS]: 'clients',
  [SERVICE_KEYS.REPORTS]: 'reports',
  [SERVICE_KEYS.ZONES]: 'zones',
  [SERVICE_KEYS.TARIFF]: 'tariff',
  [SERVICE_KEYS.SUBSCRIPTION_BLACK_WHITE]: 'subscription-blackwhite',
  [SERVICE_KEYS.EVENT_LOG]: 'events',
  [SERVICE_KEYS.HOSTS]: 'hosts',
  [SERVICE_KEYS.SERVICE_MONITORING]: 'backend',
};

const HIDDEN_MONITOR_SERVICE_KEYS = new Set([
  SERVICE_KEYS.CLIENTS,
  SERVICE_KEYS.USER_ACTION_LOG,
  SERVICE_KEYS.SERVICE_MONITORING,
]);

function* checkService(serviceKey, config, action) {
  const startedAt = Date.now();

  const response = yield makeRequest(
    config.url,
    config.params || {},
    action,
    config.method || 'GET',
    true,
  );

  const durationMs = Date.now() - startedAt;
  const httpStatus = response?.status || CONNECTION_ERROR;
  const isOnline = !isServiceUnavailableStatus(httpStatus);

  let errorMessage = '';
  if (!isOnline) {
    const payload = yield call(getBodyJson, response, null);
    errorMessage = payload?.message
      || payload?.error?.message
      || (httpStatus === CONNECTION_ERROR ? 'Проблема с подключением к сети' : 'Сервис недоступен');
  }

  return {
    id: serviceKey,
    service_key: serviceKey,
    code: SERVICE_CODES[serviceKey] || serviceKey,
    name: SERVICE_TITLES[serviceKey] || serviceKey,
    method: config.method || 'GET',
    endpoint: config.url,
    is_online: isOnline,
    http_status: httpStatus,
    latency_ms: durationMs,
    last_ping_at: new Date().toISOString(),
    error_message: errorMessage,
  };
}

const applyFilters = (list, filter = {}) => {
  let result = [...list];

  const search = String(filter?.search || '').trim().toLowerCase();
  if (search) {
    result = result.filter(item => (
      String(item.name || '').toLowerCase().includes(search)
      || String(item.code || '').toLowerCase().includes(search)
      || String(item.endpoint || '').toLowerCase().includes(search)
    ));
  }

  if (filter?.is_online === true || filter?.is_online === false) {
    result = result.filter(item => item.is_online === filter.is_online);
  }

  return result;
};

const applyPagination = (list, page, limit) => {
  const safeLimit = Math.max(1, Number(limit) || DEFAULT_ITEMS_OF_PAGE);
  const total = list.length;
  const maxPage = Math.max(1, Math.ceil(total / safeLimit));
  const safePage = Math.min(Math.max(1, Number(page) || 1), maxPage);

  const offset = (safePage - 1) * safeLimit;
  const result = list.slice(offset, offset + safeLimit);

  return {
    result,
    meta: {
      page: safePage,
      limit: safeLimit,
      total,
    },
  };
};

export default function* loadMonitorList(action) {
  try {
    const { payload = {} } = action;
    const {
      limit = DEFAULT_ITEMS_OF_PAGE,
      page = 1,
      filter = {},
    } = payload;

    yield put(loadMonitorListBegin());

    const entries = Object.entries(SERVICE_HEALTH_CHECKS)
      .filter(([serviceKey]) => !HIDDEN_MONITOR_SERVICE_KEYS.has(serviceKey));

    const list = yield all(entries.map(([serviceKey, config]) => call(checkService, serviceKey, config, action)));

    const filtered = applyFilters(list, filter);
    const { result, meta } = applyPagination(filtered, page, limit);

    const data = {
      data: result,
      result,
      meta,
    };

    yield put(loadMonitorListSuccessful(data));
    yield call(resolveActionPromise(action, data));
  } catch (e) {
    yield put(loadMonitorListFailed());
    yield call(rejectActionPromise(action, e));
    yield processError(e);
  }
}
